// Closed-set enum helper [03 BR-DM-007]: one definition gives the TS union, the zod schema and the
// value list the DB lanes turn into named CHECK constraints. No TS `enum` (erasableSyntaxOnly).
import { z } from 'zod';

export interface EnumDef<V extends string = string> {
  /** Stable name, `schema.table.column` for DB-backed sets (e.g. `sales.sales_order.status`). */
  readonly name: string;
  readonly values: readonly [V, ...V[]];
  readonly schema: z.ZodEnum<{ [K in V]: K }>;
  is(value: unknown): value is V;
}

export function defineEnum<const V extends string>(
  name: string,
  values: readonly [V, ...V[]],
): EnumDef<V> {
  if (new Set(values).size !== values.length) throw new Error(`duplicate value in enum ${name}`);
  const frozen = Object.freeze([...values]) as unknown as readonly [V, ...V[]];
  const set: ReadonlySet<string> = new Set(frozen);
  return Object.freeze({
    name,
    values: frozen,
    schema: z.enum(frozen as unknown as [V, ...V[]]) as unknown as z.ZodEnum<{ [K in V]: K }>,
    is: (value: unknown): value is V => typeof value === 'string' && set.has(value),
  });
}

/** Value union of an enum definition: `EnumValue<typeof SALES_ORDER_STATUS>`. */
export type EnumValue<E> = E extends EnumDef<infer V> ? V : never;
